import { useRef, useState, useEffect } from 'react'
import { Bell, CheckCheck, Megaphone, Calendar, MessageCircle, Star, X, BookOpen, DollarSign, ArrowRight } from 'lucide-react'
import { Link } from 'react-router-dom'
import { formatDistanceToNow, parseISO, isToday, isYesterday, isAfter, subDays } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { motion, AnimatePresence } from 'framer-motion'
import { Button } from '@/components/ui/button'
import { ScrollArea } from '@/components/ui/scroll-area'
import { useNotifications } from '@/hooks/useNotifications'
import { useAuth } from '@/hooks/useAuth'
import { cn } from '@/lib/utils'
import type { NotificationRow } from '@/types/database'

// ─── Icon by type ─────────────────────────────────────────────────────────────

function NotifIcon({ type }: { type: string }) {
  const base = 'h-4 w-4'
  switch (type) {
    case 'booking_confirmed':   return <Calendar       className={cn(base, 'text-brand-500')} />
    case 'booking_started':     return <BookOpen       className={cn(base, 'text-teal-500')} />
    case 'booking_completed':   return <CheckCheck     className={cn(base, 'text-green-500')} />
    case 'booking_cancelled':   return <X              className={cn(base, 'text-red-500')} />
    case 'booking_reminder':    return <Calendar       className={cn(base, 'text-orange-500')} />
    case 'new_message':         return <MessageCircle  className={cn(base, 'text-brand-500')} />
    case 'new_review':          return <Star           className={cn(base, 'text-yellow-500')} />
    case 'review_received':     return <Star           className={cn(base, 'text-orange-400')} />
    case 'payout_released':     return <DollarSign     className={cn(base, 'text-green-600')} />
    case 'instructor_approved': return <CheckCheck     className={cn(base, 'text-teal-600')} />
    default:                    return <Megaphone      className={cn(base, 'text-muted-foreground')} />
  }
}

// ─── Notification item ────────────────────────────────────────────────────────

function NotifItem({
  n,
  onRead,
  onClose,
}: {
  n: NotificationRow
  onRead: (id: string) => void
  onClose: () => void
}) {
  const unread = !n.read_at
  const timeAgo = formatDistanceToNow(parseISO(n.created_at), { addSuffix: true, locale: ptBR })

  const inner = (
    <div
      className={cn(
        'flex items-start gap-3 px-4 py-3 transition-colors cursor-default',
        unread ? 'bg-brand-50/60 hover:bg-brand-50' : 'hover:bg-muted/40',
      )}
      onClick={() => unread && onRead(n.id)}
    >
      <div className={cn(
        'mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full',
        unread ? 'bg-white shadow-sm border border-gray-100' : 'bg-muted',
      )}>
        <NotifIcon type={n.type ?? ''} />
      </div>

      <div className="flex-1 min-w-0">
        <p className={cn('text-sm leading-snug', unread ? 'font-semibold text-gray-900' : 'font-medium text-muted-foreground')}>
          {n.title ?? 'Notificação'}
        </p>
        {n.body && (
          <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{n.body}</p>
        )}
        <p className="text-[11px] text-muted-foreground/70 mt-1">{timeAgo}</p>
      </div>

      {unread && (
        <div className="mt-2 h-2 w-2 shrink-0 rounded-full bg-brand-500" />
      )}
    </div>
  )

  if (n.link) {
    return (
      <Link to={n.link} onClick={() => { onRead(n.id); onClose() }} className="block divide-y divide-gray-100">
        {inner}
      </Link>
    )
  }
  return <div className="divide-y divide-gray-100">{inner}</div>
}

// ─── Group by date ─────────────────────────────────────────────────────────────

type NotifGroup = { label: string; items: NotificationRow[] }

function groupByDate(list: NotificationRow[]): NotifGroup[] {
  const weekAgo = subDays(new Date(), 7)
  const groups: NotifGroup[] = [
    { label: 'Hoje', items: [] },
    { label: 'Ontem', items: [] },
    { label: 'Esta semana', items: [] },
    { label: 'Mais antigas', items: [] },
  ]
  for (const n of list) {
    const d = parseISO(n.created_at)
    if (isToday(d)) groups[0].items.push(n)
    else if (isYesterday(d)) groups[1].items.push(n)
    else if (isAfter(d, weekAgo)) groups[2].items.push(n)
    else groups[3].items.push(n)
  }
  return groups.filter((g) => g.items.length > 0)
}

// ─── Main dropdown ────────────────────────────────────────────────────────────

export function NotificationsDropdown({ variant = 'light' }: { variant?: 'light' | 'dark' }) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  const { notifications, unreadCount, markAsRead, markAllAsRead } = useNotifications()
  const { profile } = useAuth()
  const notifPath = profile?.role === 'instructor' ? '/instrutor/notificacoes/inbox' : '/aluno/notificacoes/inbox'

  // Close on outside click
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    if (open) document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [open])

  // Close on Escape
  useEffect(() => {
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false)
    }
    if (open) document.addEventListener('keydown', handleKey)
    return () => document.removeEventListener('keydown', handleKey)
  }, [open])

  async function handleMarkAll() {
    try { await markAllAsRead() } catch { /* silent */ }
  }

  async function handleRead(id: string) {
    try { await markAsRead(id) } catch { /* silent */ }
  }

  const bellClass = variant === 'dark'
    ? 'text-white/70 hover:text-white hover:bg-white/10'
    : ''

  return (
    <div ref={ref} className="relative">
      <Button
        variant="ghost"
        size="icon"
        className={cn('relative', bellClass)}
        onClick={() => setOpen((o) => !o)}
        aria-label={`Notificações${unreadCount > 0 ? ` (${unreadCount} não lidas)` : ''}`}
      >
        <Bell className="h-5 w-5" />
        <AnimatePresence>
          {unreadCount > 0 && (
            <motion.span
              key="badge"
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0 }}
              transition={{ type: 'spring', stiffness: 400, damping: 20 }}
              className="absolute -top-0.5 -right-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-red-500 text-[10px] font-bold text-white leading-none"
            >
              {unreadCount > 9 ? '9+' : unreadCount}
            </motion.span>
          )}
        </AnimatePresence>
      </Button>

      <AnimatePresence>
        {open && (
          <motion.div
            key="panel"
            initial={{ opacity: 0, y: -8, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -8, scale: 0.96 }}
            transition={{ duration: 0.15, ease: 'easeOut' }}
            className="absolute right-0 top-full mt-2 z-50 w-80 sm:w-96 rounded-2xl border border-gray-100 bg-white shadow-xl overflow-hidden"
          >
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
              <div className="flex items-center gap-2">
                <h3 className="font-semibold text-sm text-gray-900">Notificações</h3>
                {unreadCount > 0 && (
                  <span className="inline-flex items-center justify-center rounded-full bg-brand-100 text-brand-700 text-[10px] font-bold px-1.5 py-0.5">
                    {unreadCount} nova{unreadCount > 1 ? 's' : ''}
                  </span>
                )}
              </div>
              {unreadCount > 0 && (
                <button
                  onClick={handleMarkAll}
                  className="flex items-center gap-1 text-xs text-brand-600 hover:text-brand-700 font-medium transition-colors"
                >
                  <CheckCheck className="h-3.5 w-3.5" />
                  Marcar todas
                </button>
              )}
            </div>

            {/* List */}
            {notifications.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-gray-100 mb-3">
                  <Bell className="h-6 w-6 text-gray-300" />
                </div>
                <p className="text-sm font-medium text-gray-600">Nenhuma notificação</p>
                <p className="text-xs text-muted-foreground mt-1">Você está em dia!</p>
              </div>
            ) : (
              <ScrollArea className="max-h-[420px]">
                {groupByDate(notifications).map((group) => (
                  <div key={group.label}>
                    <div className="sticky top-0 z-10 px-4 py-1.5 bg-gray-50/95 backdrop-blur-sm border-b border-gray-100">
                      <span className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground/60">
                        {group.label}
                      </span>
                    </div>
                    <div className="divide-y divide-gray-100">
                      {group.items.map((n) => (
                        <NotifItem
                          key={n.id}
                          n={n}
                          onRead={handleRead}
                          onClose={() => setOpen(false)}
                        />
                      ))}
                    </div>
                  </div>
                ))}
              </ScrollArea>
            )}

            {/* Footer */}
            {notifications.length > 0 && (
              <div className="border-t border-gray-100 bg-gray-50 px-4 py-2.5 flex items-center justify-between">
                <p className="text-[11px] text-muted-foreground">
                  {notifications.length} notificações
                </p>
                <Link
                  to={notifPath}
                  onClick={() => setOpen(false)}
                  className="flex items-center gap-1 text-xs font-medium text-brand-600 hover:text-brand-700 transition-colors"
                >
                  Ver todas
                  <ArrowRight className="h-3 w-3" />
                </Link>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}
